import { Col, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFillLarge from "@amzn/stencil-react-icons/icons/icon-check-circle-fill-large";
import React, { useContext, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { VerificationStatus } from "@/apis/IdentityCheckHelper";
import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import { ITS_LIGHT_TEAL } from "@/components/constants";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

export const DATA_TEST_ID_VERIFICATION_COMPLETED = "verificationCompletedView";
export const VerCompletedMainViewStyle: Record<string, string> = {
  display: "flex",
  backgroundColor: ITS_LIGHT_TEAL,
  padding: "S600",
  justifyContent: "center",
  alignItems: "center",
};

/**
 * A view that informs NHs that their identities have been verified successfully.
 */
export default function VerificationCompleted(): JSX.Element {
  const { state, dispatch } = useContext(AppContext);

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    switch (state.docsVerificationStatus) {
      case VerificationStatus.FULL_SUCCESS: {
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-manual-full-pass",
        });
        break;
      }
      case VerificationStatus.LIGHT_SUCCESS: {
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: "ezo-identity-check-manual-lightweight-pass",
        });
        break;
      }
      default: {
        // For other verification statuses, use the step's default refPage
        // This maintains hierarchical defaults: Step Default > Global Default
        dispatch({
          type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
          chatSupportRefPage: state.currentStep?.defaultChatSupportRefPage,
        });
      }
    }
  }, [state.docsVerificationStatus]);

  useEffect(() => {
    if (
      state.docsVerificationStatus === VerificationStatus.FULL_SUCCESS ||
      state.docsVerificationStatus === VerificationStatus.LIGHT_SUCCESS ||
      state.docsVerificationStatus === VerificationStatus.COMPLETED
    ) {
      updateOnboardingStep("D1_IDV_COMPLETE");
    }

    switch (state.docsVerificationStatus) {
      case VerificationStatus.FULL_SUCCESS: {
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_FULL_RIV_RESULT_SUCCESS",
        });
        break;
      }

      case VerificationStatus.LIGHT_SUCCESS: {
        dispatch({
          type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
          nonDirectedChatSupportContext: "ON_LIGHT_RIV_RESULT_SUCCESS",
        });
        break;
      }
      default:
        // do nothing
        break;
    }
  }, [state.docsVerificationStatus]);

  return (
    <View
      {...VerCompletedMainViewStyle}
      dataTestId={DATA_TEST_ID_VERIFICATION_COMPLETED}
    >
      <Col gridGap="S500">
        <StepBodyStyledText>
          <Text fontWeight="regular" textAlign="center">
            <FormattedMessage id="identityCheck-verified-successMsg" />
          </Text>
        </StepBodyStyledText>
        <View
          display="flex"
          color="green60"
          justifyContent="center"
          alignItems="center"
        >
          <IconCheckCircleFillLarge
            aria-hidden
            dataTestId="iconCheckCircleDataTestId"
          />
        </View>
      </Col>
    </View>
  );
}
